import React, { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ContactUs from './ContactUs'
import { verifyToken, getToken } from '../utils/auth'

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const isValid = await verifyToken();
          if (isValid) {
            navigate('/home', { replace: true });
            return;
          }
        } catch (err) {
          console.error('Token verification failed:', err);
        }
      }
    };

    checkAuth();
  }, [navigate]);

  const FEATURES = [
    { title: 'Automated Workflow Optimization', desc: 'Convert conversations, media, and voice messages into actionable insights and order-ready content — quickly and reliably.' },
    { title: 'Designed for Mobile Teams', desc: 'A lightweight and responsive interface built for fast-paced environments. Get things done on the go, with less effort.' },
    { title: 'Smart Suggestions & Integration', desc: 'Enjoy intelligent recommendations and seamless syncing with your tools. No more guesswork or retyping — just smooth handoffs.' },
  ]

  const AUDIENCES = [
    { title: 'For Sales Teams', icon: '/icons/mobile-security.png', text: 'Focus on relationships and outcomes, not manual processes. Our platform streamlines routine tasks, allowing sales representatives to dedicate their time to what matters most.' },
    { title: 'For Businesses', icon: '/icons/improved-orders-display.png', text: 'Accelerate operations and reduce human error by letting automation handle routine inputs and deliver consistent results.' },
  ]

  return (
    <div className="relative min-h-screen font-sans flex flex-col text-[var(--text-primary)] bg-gradient-to-b from-[var(--bg-start)] to-[var(--bg-end)]">
      <header className="fixed top-0 left-0 right-0 z-50 w-full flex justify-between items-center p-4 md:px-10 bg-[var(--accent-secondary)] shadow-md">
        <Link to="/" className="flex items-center">
          <img src="/images/logo.png" alt="Toritech Logo" className="w-8 h-8 mr-2" />
          <h1 className="text-xl md:text-3xl font-bold tracking-wide text-white">Toritech</h1>
        </Link>
        <div className="flex gap-2 md:gap-4">
          <Link 
            to="/login" 
            className="px-3 md:px-4 py-1.5 md:py-2 text-sm font-medium border border-white rounded hover:bg-[var(--hover-light)] transition whitespace-nowrap text-white"
          >
            Login
          </Link>
          <Link 
            to="/signup" 
            className="px-3 md:px-4 py-1.5 md:py-2 text-sm font-medium bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] transition whitespace-nowrap"
          >
            Sign Up
          </Link>
        </div>
      </header>

      <div className="h-[72px]"></div>

      <div className="flex-1">
        <main className="flex flex-col items-center justify-center text-center px-6 pt-20">
          <h2 className="text-5xl md:text-6xl font-bold mb-12 text-[var(--accent-primary)]">
            AI-Powered Tools for Automated Sales
          </h2>
          <p className="text-lg md:text-xl max-w-2xl mb-8">
            Toritech simplifies the sales process by transforming conversations into actionable insights and intelligent recommendations — delivering a streamlined, efficient workflow.
          </p>
        </main>

        <section className="mt-20 px-6 max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {FEATURES.map((item, idx) => (
            <div 
              key={idx} 
              className="p-6 rounded-lg bg-white border border-[var(--border-color)] hover:translate-y-[-4px] transition-all duration-300"
              style={{ boxShadow: '0 10px 25px rgba(94, 53, 177, 0.1)' }}
            >
              <h4 className="text-xl font-semibold mb-2 text-[var(--accent-primary)]">{item.title}</h4>
              <p className="text-[var(--text-secondary)]">{item.desc}</p>
            </div>
          ))}
        </section>

        <section className="mt-20 px-6 max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold mb-6 text-[var(--accent-secondary)]">Why Toritech?</h3>
          <p className="text-[var(--text-secondary)] text-lg">
            Our platform delivers intelligent automation, smart recommendations, and seamless integrations — enabling your team to work smarter, not harder.
          </p>
        </section>

        <section className="mt-20 px-6 max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          {AUDIENCES.map((item, idx) => (
            <div 
              key={idx} 
              className="flex flex-col items-center p-6 rounded-lg bg-white border border-[var(--border-color)] text-center hover:translate-y-[-4px] transition-all duration-300"
              style={{ boxShadow: '0 10px 25px rgba(94, 53, 177, 0.1)' }}
            >
              <div className="w-32 h-32 flex items-center justify-center bg-[var(--accent-primary)] rounded-full mb-3 shadow-md">
                <img 
                  src={item.icon} 
                  alt={item.title} 
                  className="w-24 h-24 object-contain p-2" 
                />
              </div>
              <h4 className="text-2xl font-semibold mb-2 text-[var(--accent-secondary)]">{item.title}</h4>
              <p className="text-[var(--text-secondary)]">{item.text}</p>
            </div>
          ))}
        </section>

        <section className="mt-20 px-6 py-10 max-w-xl mx-auto text-center bg-[var(--accent-secondary)] text-white rounded-lg shadow-lg">
          <h3 className="text-3xl font-bold mb-4">Ready to Get Started?</h3>
          <p className="mb-6 text-base md:text-lg">
            Explore how Toritech can enhance your sales workflow with intelligent automation and robust integrations.
          </p>
          <ContactUs />
        </section>

        <footer className="text-center mt-16 mb-8 text-[var(--text-secondary)] text-sm">
          © {new Date().getFullYear()} Toritech Automation
        </footer>
      </div>
    </div>
  )
}
